import { useMemo, useCallback, useEffect } from 'react';
import { AccommodationListingDTO, SortOption } from '../AccommodationsConsole.types';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useAccommodationsSortOrder } from './useAccommodationsSortOrder';
import { sortByFavorite, sortByOrderList } from '@shared/utils/array';

export const useListingSort = (listings: AccommodationListingDTO[], venueCoordinates?: { latitude: number; longitude: number }) => {
  const queryParamHelper = useQueryParamHelper();
  const updateSearchParam = useUpdateSearchParam();
  const sortOption = (queryParamHelper.getValueString('sort') as SortOption) || SortOption.COUPLES_PICKS;

  const { pageContainerBySlug } = useAccommodationsSortOrder();

  // Set default sort in URL if not present
  useEffect(() => {
    if (!queryParamHelper.getValueString('sort')) {
      updateSearchParam('sort', SortOption.COUPLES_PICKS);
    }
  }, [queryParamHelper, updateSearchParam]);

  const updateSort = useCallback(
    (newSort: SortOption) => {
      updateSearchParam('sort', newSort);
    },
    [updateSearchParam]
  );

  const sortedListings = useMemo(() => {
    const listingsCopy = [...listings];

    switch (sortOption) {
      case SortOption.COUPLES_PICKS:
        const blockOrdering = pageContainerBySlug?.blockOrdering;
        const coupleListings = listingsCopy.filter(listing => listing.isPickedByCouple);
        const nonCoupleListings = listingsCopy.filter(listing => !listing.isPickedByCouple);
        const sortedCoupleListings = sortByFavorite(sortByOrderList(coupleListings, blockOrdering ?? []));
        return [...sortedCoupleListings, ...nonCoupleListings];

      case SortOption.PRICE_LOW_TO_HIGH:
        return listingsCopy.sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? Number.MAX_VALUE;
          return aPrice - bPrice;
        });

      case SortOption.PRICE_HIGH_TO_LOW:
        return listingsCopy.sort((a, b) => {
          const aPrice = a.pricingInfo?.values?.nightlyBaseRate ?? -1;
          const bPrice = b.pricingInfo?.values?.nightlyBaseRate ?? -1;
          return bPrice - aPrice;
        });

      case SortOption.MOST_SAVINGS:
        return listingsCopy.sort((a, b) => {
          const aDiscount = a.pricingInfo?.values?.discountPercentage ?? 0;
          const bDiscount = b.pricingInfo?.values?.discountPercentage ?? 0;
          return bDiscount - aDiscount;
        });

      case SortOption.DISTANCE:
        // Sort by distance from venue using coordinates
        return listingsCopy.sort((a, b) => {
          if (venueCoordinates) {
            const aDistance = distanceInMiles({ lat: venueCoordinates.latitude, lng: venueCoordinates.longitude }, a.location) ?? Number.MAX_VALUE;
            const bDistance = distanceInMiles({ lat: venueCoordinates.latitude, lng: venueCoordinates.longitude }, b.location) ?? Number.MAX_VALUE;
            return aDistance - bDistance;
          }
          return 0;
        });

      default:
        return listingsCopy;
    }
  }, [listings, pageContainerBySlug?.blockOrdering, sortOption, venueCoordinates]);

  return {
    sortedListings,
    sortOption,
    updateSort
  };
};
